/* eslint-disable ts/consistent-type-definitions */
import { z } from 'zod';

import { authMiddleware, client, createAuthMiddleware } from '..';

declare global {
    interface Window {
        reevaluateAuthentication: () => void;
    }
}

export const LOCAL_STORAGE_KEY = 'authentication' as const;

export const tokenSchema = z.object({
    accessToken: z.string(),
    tokenType: z.string(),
    expiresIn: z.number(),
    issuedAt: z.string(),
});

export type Token = z.infer<typeof tokenSchema>;

export function getToken(): Token['accessToken'] | null {
    const token = getAuthentication()?.accessToken ?? null;

    return token;
}

export function getAuthentication(): Token | null {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!data)
        return null;

    const token = tokenSchema.safeParse(JSON.parse(data));

    if (!token.success) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);

        return null;
    }

    return token.data;
}

export function setAuthentication(data: Token) {
    const token = tokenSchema.safeParse(data);

    if (!token.success)
        throw new Error('Invalid token data');

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(token.data));

    client.eject(authMiddleware);
    client.use(createAuthMiddleware({ token: token.data.accessToken }));
}

export function isTokenExpired(): 'EXPIRED' | 'VALID' | 'NO_TOKEN' {
    const token = getAuthentication();

    if (!token)
        return 'NO_TOKEN';

    const now = Date.now();
    const expiresAt = new Date(token.issuedAt).getTime() + token.expiresIn * 1000;

    return now > expiresAt ? 'EXPIRED' : 'VALID';
}

export function removeAuthentication() {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
}

export function isAuthenticated(): boolean {
    const status = isTokenExpired();
    if (status === 'EXPIRED') {
        removeAuthentication();
        return false;
    }
    return status === 'VALID';
}

export function reevaluateAuthentication() {
    const token = getAuthentication();

    if (!token) {
        throw new Error('No token found');
    }

    setAuthentication(token);
}

window.reevaluateAuthentication = reevaluateAuthentication;
